import { onboardingWorkingHoursSchema } from '../onboarding/onboarding-profile-contracts'
import { calendarDateKey } from './calendar-model'

export type ClinicDayHours = {
  status: 'open' | 'closed' | 'fallback'
  startMinute: number
  endMinute: number
  label: string
  source: 'weekly' | 'special' | 'fallback'
  intervals: Array<{ startMinute: number; endMinute: number }>
}

const FALLBACK_START_MINUTE = 7 * 60
const FALLBACK_END_MINUTE = 20 * 60
const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const
const INTERVAL = /^(?:[01]\d|2[0-3]):[0-5]\d-(?:[01]\d|2[0-3]):[0-5]\d$/

function minute(value: string) {
  const [hour = 0, minutes = 0] = value.split(':').map(Number)
  return hour * 60 + minutes
}

function parseInterval(value: unknown) {
  if (typeof value !== 'string' || !INTERVAL.test(value)) return null
  if (value === '00:00-00:00') return { closed: true as const }
  const startMinute = minute(value.slice(0, 5))
  const endMinute = minute(value.slice(6))
  return endMinute > startMinute
    ? { closed: false as const, startMinute, endMinute }
    : null
}

function timeLabel(minutes: number) {
  const hour = Math.floor(minutes / 60)
  const minuteValue = minutes % 60
  const suffix = hour >= 12 ? 'PM' : 'AM'
  const displayHour = hour % 12 || 12
  return minuteValue
    ? `${displayHour}:${String(minuteValue).padStart(2, '0')} ${suffix}`
    : `${displayHour} ${suffix}`
}

function dateFromTimestamp(value: unknown, timeZone: string) {
  return typeof value === 'number' && Number.isFinite(value)
    ? calendarDateKey(value, timeZone)
    : null
}

function specialInterval(
  value: unknown,
  date: string,
  timeZone: string
): unknown {
  if (!value || typeof value !== 'object') return undefined
  if (!Array.isArray(value)) {
    const direct = (value as Record<string, unknown>)[date]
    if (typeof direct === 'string') return direct
  }

  const records = Array.isArray(value)
    ? value
    : Object.values(value as Record<string, unknown>)
  const matches = records.flatMap((candidate) => {
    if (
      !candidate ||
      typeof candidate !== 'object' ||
      Array.isArray(candidate)
    ) {
      return []
    }
    const record = candidate as Record<string, unknown>
    const recordDate =
      typeof record.date === 'string'
        ? record.date
        : dateFromTimestamp(record.dateTimestamp, timeZone)
    if (recordDate !== date) return []
    if (record.status === 'CLOSED' || record.status === 'Closed') {
      return ['00:00-00:00']
    }
    if (
      record.status === 'OPEN' &&
      typeof record.start === 'string' &&
      typeof record.end === 'string'
    ) {
      return [`${record.start}-${record.end}`]
    }
    return typeof record.updatedTime === 'string' ? [record.updatedTime] : []
  })
  return matches.length === 1 ? matches[0] : undefined
}

function fallbackHours(): ClinicDayHours {
  return {
    status: 'fallback',
    startMinute: FALLBACK_START_MINUTE,
    endMinute: FALLBACK_END_MINUTE,
    label: 'Clinic hours unavailable · showing the safe schedule range',
    source: 'fallback',
    intervals: [
      { startMinute: FALLBACK_START_MINUTE, endMinute: FALLBACK_END_MINUTE },
    ],
  }
}

export function resolveClinicDayHours(
  settings: unknown,
  date: string,
  timeZone: string | null
): ClinicDayHours {
  if (!timeZone || !settings || typeof settings !== 'object') {
    return fallbackHours()
  }
  try {
    new Intl.DateTimeFormat('en-US', { timeZone }).format(0)
  } catch {
    return fallbackHours()
  }
  const record = settings as Record<string, unknown>
  const effective = record.effectiveSchedulingHours
  if (effective && typeof effective === 'object') {
    const effectiveRecord = effective as Record<string, unknown>
    const weekly = effectiveRecord.weekly
    const overrides = effectiveRecord.dateOverrides
    const dayIndex = new Date(`${date}T00:00:00Z`).getUTCDay()
    const dayKey = DAY_KEYS[dayIndex]!
    const selected =
      overrides && typeof overrides === 'object' && date in overrides
        ? (overrides as Record<string, unknown>)[date]
        : weekly && typeof weekly === 'object'
          ? (weekly as Record<string, unknown>)[dayKey]
          : null
    if (Array.isArray(selected)) {
      const intervals = selected.flatMap((item) => {
        if (!item || typeof item !== 'object') return []
        const candidate = item as Record<string, unknown>
        if (
          typeof candidate.start !== 'string' ||
          typeof candidate.end !== 'string'
        ) {
          return []
        }
        const interval = parseInterval(`${candidate.start}-${candidate.end}`)
        return interval && !interval.closed ? [interval] : []
      })
      if (!intervals.length) {
        return {
          status: 'closed',
          startMinute: 0,
          endMinute: 0,
          label: 'Closed for Flio scheduling',
          source: 'special',
          intervals: [],
        }
      }
      const startMinute = Math.min(...intervals.map((item) => item.startMinute))
      const endMinute = Math.max(...intervals.map((item) => item.endMinute))
      return {
        status: 'open',
        startMinute,
        endMinute,
        label: `Effective scheduling hours ${timeLabel(startMinute)}–${timeLabel(endMinute)}`,
        source:
          overrides && typeof overrides === 'object' && date in overrides
            ? 'special'
            : 'weekly',
        intervals: intervals
          .map(({ startMinute: start, endMinute: end }) => ({
            startMinute: start,
            endMinute: end,
          }))
          .sort((left, right) => left.startMinute - right.startMinute),
      }
    }
  }
  const parsedHours = onboardingWorkingHoursSchema.safeParse(
    record.workingHours
  )
  if (!parsedHours.success) return fallbackHours()

  const special = specialInterval(record.specialDays, date, timeZone)
  const dayIndex = new Date(`${date}T00:00:00Z`).getUTCDay()
  const weekly = parsedHours.data[DAY_KEYS[dayIndex]!]
  const interval = parseInterval(special ?? weekly)
  if (!interval) return fallbackHours()
  const source = special === undefined ? 'weekly' : 'special'
  if (interval.closed) {
    return {
      status: 'closed',
      startMinute: 0,
      endMinute: 0,
      label:
        source === 'special' ? 'Clinic closed · special day' : 'Clinic closed',
      source,
      intervals: [],
    }
  }
  return {
    status: 'open',
    startMinute: interval.startMinute,
    endMinute: interval.endMinute,
    label: `Clinic hours ${timeLabel(interval.startMinute)}–${timeLabel(interval.endMinute)}`,
    source,
    intervals: [
      {
        startMinute: interval.startMinute,
        endMinute: interval.endMinute,
      },
    ],
  }
}
